import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { EVENT, TEX } from "../core/keys"
import {
  BUMPER_LIFETIME,
  BUMPER_PLAYER_HIT_COOLDOWN,
  BUMPER_SIZE_DEFS,
  BUMPER_TYPES,
  COMBO_TARGET_HITS,
  GAME_HEIGHT,
  GAME_WIDTH,
  LANE_MID_Y,
  LANE_TOP_Y,
  SCENE_TUNING,
  SLINGSHOT_KICK_ANGLE,
  SLINGSHOT_LIFETIME,
  SLINGSHOT_TYPE,
} from "../data/constants"
import type {
  BumperEffectType,
  BumperEntityData,
  BumperSize,
  ComboChain,
  GameSprite,
  LanePosition,
} from "../types"

const BUMPER_HIT_VISUALS: Record<
  BumperEffectType,
  { pulse: number; ringMs: number }
> = {
  charge: { pulse: 1.15, ringMs: 160 },
  guard: { pulse: 1.12, ringMs: 170 },
  bank: { pulse: 1.22, ringMs: 200 },
  levelup: { pulse: 1.1, ringMs: 170 },
  heal: { pulse: 1.16, ringMs: 180 },
}

export class BumperSystem {
  private scene: Phaser.Scene
  private bumperGroup: Phaser.Physics.Arcade.Group

  constructor(scene: Phaser.Scene, bumperGroup: Phaser.Physics.Arcade.Group) {
    this.scene = scene
    this.bumperGroup = bumperGroup
  }

  // ── Spawning ────────────────────────────────────────────────

  public spawnBumper(bumperWeights?: Record<BumperEffectType, number>): void {
    const bumperX =
      GAME_WIDTH +
      SCENE_TUNING.bumperSpawnBaseOffsetX +
      Math.random() * SCENE_TUNING.bumperSpawnVarianceX

    const bumperY =
      SCENE_TUNING.bumperSpawnVerticalMargin +
      Math.random() * (GAME_HEIGHT - SCENE_TUNING.bumperSpawnVerticalSafeZone)

    // ── Select bumper type (effect) ──────────────────────────────
    const availableTypes = BUMPER_TYPES
    const fallbackType = availableTypes[0]
    if (!fallbackType) return

    const bumperType = bumperWeights
      ? this.selectBumperType(bumperWeights, availableTypes)
      : (availableTypes[Math.floor(Math.random() * availableTypes.length)] ??
        fallbackType)

    // ── Select bumper size (weighted random) ─────────────────────
    const size = this.pickBumperSize()
    const sizeDef = BUMPER_SIZE_DEFS[size]

    // Pool-based acquisition
    const bumper = this.bumperGroup.get(
      bumperX,
      bumperY,
      bumperType.textureKey,
    ) as GameSprite | null
    if (!bumper) return

    bumper.setActive(true)
    bumper.setVisible(true)
    bumper.setPosition(bumperX, bumperY)
    bumper.setTexture(bumperType.textureKey)
    bumper.setDepth(35)

    // Scale sprite based on size
    const baseScale = sizeDef.radius / bumperType.radius
    bumper.setScale(baseScale)

    const body = bumper.body as Phaser.Physics.Arcade.Body
    if (body) {
      body.setAllowGravity(false)
      body.setImmovable(true)
      body.setCircle(
        Math.floor(sizeDef.radius * SCENE_TUNING.bumperCircleBodyScale),
        sizeDef.radius * SCENE_TUNING.bumperCircleBodyOffsetScale,
        sizeDef.radius * SCENE_TUNING.bumperCircleBodyOffsetScale,
      )
    }

    const effect = bumperType.effect
    const lane: LanePosition =
      bumperY < LANE_TOP_Y ? "top" : bumperY < LANE_MID_Y ? "mid" : "bottom"

    const entityData: BumperEntityData = {
      kind: "bumper",
      x: bumperX,
      y: bumperY,
      radius: sizeDef.radius,
      color: bumperType.color,
      points: sizeDef.points,
      effect,
      lane,
      lifetime: BUMPER_LIFETIME,
      wobbleSeed: Math.random() * SCENE_TUNING.bumperWobbleSeedRange,
      targetYOffset: Phaser.Math.Between(
        SCENE_TUNING.bumperTargetYOffsetMin,
        SCENE_TUNING.bumperTargetYOffsetMax,
      ),
      size,
      stunTimer: 0,
      baseScrollSpeed: SCENE_TUNING.bumperVelocityBaseScale,
      contactDamage: 0,
      playerHitCooldown: 0,
      playerContactLocked: false,
    }
    bumper.entityData = entityData

    // Intro tween
    bumper.setScale(0)
    this.scene.tweens.add({
      targets: bumper,
      scale: { from: 0, to: baseScale },
      duration: SCENE_TUNING.bumperIntroTweenDurationMs,
      ease: "Back.easeOut",
    })

    // Schedule removal
    this.scene.time.delayedCall(BUMPER_LIFETIME, () => {
      if (bumper.active) {
        this.scene.tweens.add({
          targets: bumper,
          scale: 0,
          alpha: 0,
          duration: SCENE_TUNING.bumperExitTweenDurationMs,
          onComplete: () => {
            if (bumper.active) {
              bumper.setActive(false)
              bumper.setVisible(false)
            }
          },
        })
      }
    })
  }

  // ── Slingshot spawning ─────────────────────────────────────

  /**
   * Spawns a slingshot deflector into the bumperGroup.
   * Slingshotss are indestructible chevron-shaped bumpers that kick combat
   * balls in a fixed direction. They appear near the top or bottom thirds of
   * the field and stay until their lifetime expires.
   */
  public spawnSlingshot(_wave: number): void {
    const slingshotX =
      GAME_WIDTH +
      SCENE_TUNING.bumperSpawnBaseOffsetX +
      Math.random() * SCENE_TUNING.bumperSpawnVarianceX

    // Alternate between top-zone and bottom-zone each call
    const useTop = Math.random() < 0.5
    const edgeMargin = 60
    const slingshotY = useTop
      ? edgeMargin + Math.random() * (GAME_HEIGHT * 0.28 - edgeMargin)
      : GAME_HEIGHT * 0.72 +
        Math.random() * (GAME_HEIGHT - edgeMargin - GAME_HEIGHT * 0.72)

    // Kick angle: top slingshot sends ball down-right; bottom sends it up-right
    const kickAngle = useTop ? SLINGSHOT_KICK_ANGLE : -SLINGSHOT_KICK_ANGLE

    const slingshot = this.bumperGroup.get(
      slingshotX,
      slingshotY,
      TEX.BUMPER_SLINGSHOT,
    ) as GameSprite | null
    if (!slingshot) return

    slingshot.setActive(true)
    slingshot.setVisible(true)
    slingshot.setPosition(slingshotX, slingshotY)
    slingshot.setTexture(TEX.BUMPER_SLINGSHOT)
    slingshot.setDisplaySize(
      SLINGSHOT_TYPE.displayWidth,
      SLINGSHOT_TYPE.displayHeight,
    )

    // Rotate sprite so the tip points in the kick direction
    slingshot.setAngle(kickAngle * (180 / Math.PI))

    const body = slingshot.body as Phaser.Physics.Arcade.Body
    if (body) {
      body.setAllowGravity(false)
      body.setImmovable(true)
      body.setCircle(
        SLINGSHOT_TYPE.radius,
        SLINGSHOT_TYPE.bodyOffsetX,
        SLINGSHOT_TYPE.bodyOffsetY,
      )
    }

    const lane: LanePosition =
      slingshotY < LANE_TOP_Y
        ? "top"
        : slingshotY < LANE_MID_Y
          ? "mid"
          : "bottom"

    const entityData: BumperEntityData = {
      kind: "bumper",
      x: slingshotX,
      y: slingshotY,
      radius: SLINGSHOT_TYPE.radius,
      color: SLINGSHOT_TYPE.color,
      points: SLINGSHOT_TYPE.points,
      effect: SLINGSHOT_TYPE.effect,
      slingshotAngle: kickAngle,
      lane,
      lifetime: SLINGSHOT_LIFETIME,
      wobbleSeed: 0,
      targetYOffset: useTop ? -GAME_HEIGHT : GAME_HEIGHT,
      size: "small",
      stunTimer: 0,
      baseScrollSpeed: SCENE_TUNING.bumperVelocityBaseScale,
      contactDamage: 0,
      playerHitCooldown: 0,
      playerContactLocked: false,
    }
    slingshot.entityData = entityData

    // No hitText for slingshotss — they're structural

    // Intro tween
    slingshot.setScale(0)
    this.scene.tweens.add({
      targets: slingshot,
      scale: { from: 0, to: 1 },
      duration: SCENE_TUNING.bumperIntroTweenDurationMs,
      ease: "Back.easeOut",
    })

    // Schedule removal
    this.scene.time.delayedCall(SLINGSHOT_LIFETIME, () => {
      if (slingshot.active) {
        this.scene.tweens.add({
          targets: slingshot,
          scale: 0,
          alpha: 0,
          duration: SCENE_TUNING.bumperExitTweenDurationMs,
          onComplete: () => {
            slingshot.setActive(false)
            slingshot.setVisible(false)
            slingshot.setAlpha(1)
          },
        })
      }
    })
  }

  // ── Ball hits bumper — pure visual feedback ─────────────────

  public onBallHitBumper(bumperSprite: GameSprite): void {
    const bd = bumperSprite.entityData
    if (!bd || bd.kind !== "bumper") return

    const flashColor =
      bd.slingshotAngle !== undefined
        ? 0x84cc16
        : Phaser.Display.Color.HexStringToColor(bd.color).color
    const feedback =
      bd.slingshotAngle !== undefined
        ? { pulse: 1.22, ringMs: 180 }
        : BUMPER_HIT_VISUALS[bd.effect]
    bumperSprite.setTint(flashColor)
    this.scene.time.delayedCall(100, () => {
      if (bumperSprite.active) bumperSprite.clearTint()
    })

    // Physical feedback: scale pulse
    const baseScale = bumperSprite.scaleX
    this.scene.tweens.add({
      targets: bumperSprite,
      scale: baseScale * feedback.pulse,
      duration: 60,
      yoyo: true,
      ease: "Back.easeOut",
    })

    const spark = this.scene.add.circle(
      bumperSprite.x,
      bumperSprite.y,
      bd.radius * 0.7,
    )
    spark.setStrokeStyle(2, flashColor, 0.75)
    spark.setBlendMode(Phaser.BlendModes.ADD)
    spark.setDepth(90)
    this.scene.tweens.add({
      targets: spark,
      scale: 1.8,
      alpha: 0,
      duration: feedback.ringMs,
      ease: "Quad.easeOut",
      onComplete: () => spark.destroy(),
    })
  }

  // ── Update bumper stun timers ──────────────────────────────

  public updateBumperStun(delta: number): void {
    for (const child of this.bumperGroup.children) {
      const sprite = child as GameSprite
      if (!sprite.active) continue
      const bd = sprite.entityData
      if (!bd || bd.kind !== "bumper") continue

      // Tick player hit cooldown
      if (bd.playerHitCooldown > 0) {
        bd.playerHitCooldown -= delta
      }

      if (bd.stunTimer > 0) {
        bd.stunTimer = Math.max(0, bd.stunTimer - delta)
      }
    }
  }

  /** Get speed ratio for a bumper. Persistent bumpers always move at base speed. */
  public getBumperSpeedRatio(_bd: BumperEntityData): number {
    return 1.0
  }

  public canTriggerPlayerContact(
    bumperSprite: GameSprite,
    distance: number,
    contactDistance: number,
  ): boolean {
    const bd = bumperSprite.entityData
    if (!bd || bd.kind !== "bumper") return false

    const releaseDistance =
      contactDistance + SCENE_TUNING.bumperPlayerRearmPadding

    if (bd.playerContactLocked) {
      if (distance >= releaseDistance) {
        bd.playerContactLocked = false
      }
      return false
    }

    return bd.playerHitCooldown <= 0 && distance < contactDistance
  }

  public registerPlayerContact(bumperSprite: GameSprite): void {
    const bd = bumperSprite.entityData
    if (!bd || bd.kind !== "bumper") return

    bd.playerHitCooldown = BUMPER_PLAYER_HIT_COOLDOWN
    bd.playerContactLocked = true
  }

  // ── Bumper size selection ──────────────────────────────────

  private pickBumperSize(): BumperSize {
    const roll = Math.random()
    if (roll < BUMPER_SIZE_DEFS.small.spawnWeight) return "small"
    if (
      roll <
      BUMPER_SIZE_DEFS.small.spawnWeight + BUMPER_SIZE_DEFS.medium.spawnWeight
    )
      return "medium"
    return "large"
  }

  // ── Combo counter ───────────────────────────────────────────

  public registerComboHit(
    combo: ComboChain,
    effect: BumperEffectType,
    color: string,
    charge = 1,
  ): boolean {
    const prevHits = combo.hits
    const prevTier = this.getComboTier(prevHits, combo.targetHits)
    const chargeAmount = Math.max(1, Math.floor(charge))
    combo.hits = Math.min(combo.targetHits, combo.hits + chargeAmount)
    combo.nextEffect = effect

    if (!combo.hitColors || prevHits === 0) combo.hitColors = []
    for (let i = 0; i < chargeAmount; i++) {
      combo.hitColors.push(color)
    }

    EventBus.emit(EVENT.COMBO_STATE_CHANGED, {
      state: combo.state,
      hits: combo.hits,
      targetHits: combo.targetHits,
      nextEffect: combo.nextEffect,
      hitColors: combo.hitColors,
    })

    const currentTier = this.getComboTier(combo.hits, combo.targetHits)
    if (currentTier > prevTier) {
      EventBus.emit(EVENT.COMBO_TIER_REACHED, {
        tier: currentTier,
        hits: combo.hits,
        targetHits: combo.targetHits,
        nextEffect: combo.nextEffect,
      })
    }

    return combo.hits >= combo.targetHits
  }

  public getMostFrequentColor(combo: ComboChain): string {
    if (!combo.hitColors || combo.hitColors.length === 0) return "#ffffff"
    const counts: Record<string, number> = {}
    let maxCount = 0
    let mostFrequent = combo.hitColors[0]
    for (const c of combo.hitColors) {
      counts[c] = (counts[c] || 0) + 1
      if (counts[c] > maxCount) {
        maxCount = counts[c]
        mostFrequent = c
      }
    }
    return mostFrequent
  }

  public setComboPowered(
    combo: ComboChain,
    durationMs: number,
    effect: BumperEffectType,
  ): void {
    combo.state = "powered"
    combo.hits = 0
    combo.timer = durationMs
    combo.nextEffect = effect
    // Do not clear hitColors here yet, we might need it for the activation visual
    EventBus.emit(EVENT.COMBO_STATE_CHANGED, {
      state: combo.state,
      hits: combo.hits,
      targetHits: combo.targetHits,
      nextEffect: combo.nextEffect,
    })
  }

  public resetCombo(combo: ComboChain): void {
    combo.state = "normal"
    combo.hits = 0
    combo.timer = 0
    combo.nextEffect = "charge"
    combo.hitColors = []
    EventBus.emit(EVENT.COMBO_STATE_CHANGED, {
      state: combo.state,
      hits: combo.hits,
      targetHits: combo.targetHits,
      nextEffect: combo.nextEffect,
      hitColors: combo.hitColors,
    })
  }

  public explodeAll(): void {
    const toExplode: GameSprite[] = []
    for (const child of this.bumperGroup.children) {
      const bumper = child as GameSprite
      if (!bumper.active) continue

      // Only explode bumpers that are on screen
      if (bumper.x > 0 && bumper.x < GAME_WIDTH) {
        toExplode.push(bumper)
      }
    }

    for (const bumper of toExplode) {
      const ed = bumper.entityData as BumperEntityData
      if (!ed) continue

      // Trigger explosion VFX
      const waveScene = this.scene as Phaser.Scene & {
        vfx?: {
          emitExplosionBurst: (
            x: number,
            y: number,
            color: number,
            scale: number,
          ) => void
        }
      }
      if (waveScene.vfx) {
        waveScene.vfx.emitExplosionBurst(bumper.x, bumper.y, 0x00ffff, 1.2)
      }

      // Deactivate bumper
      bumper.setActive(false)
      bumper.setVisible(false)
      if (bumper.body) bumper.body.enable = false
    }
  }

  public getComboTimerPercent(combo: ComboChain): number {
    if (combo.targetHits <= 0) return 0
    return Math.max(0, Math.min(1, combo.hits / combo.targetHits))
  }

  public ensureComboDefaults(combo: ComboChain): void {
    combo.state = combo.state ?? "normal"
    combo.hits = combo.hits ?? 0
    combo.targetHits = combo.targetHits ?? COMBO_TARGET_HITS
    combo.nextEffect = combo.nextEffect ?? "charge"
    combo.timer = combo.timer ?? 0
    combo.hitColors = combo.hitColors ?? []
  }

  private getComboTier(hits: number, targetHits: number): number {
    if (hits <= 0 || targetHits <= 0) return 0
    const progress = Math.max(0, Math.min(1, hits / targetHits))
    return Math.max(1, Math.min(4, Math.ceil(progress * 4)))
  }

  // ── Weighted bumper type selection ─────────────────────────

  private selectBumperType(
    weights: Record<BumperEffectType, number>,
    availableTypes: readonly (typeof BUMPER_TYPES)[number][],
  ): (typeof BUMPER_TYPES)[number] {
    const fallbackType = availableTypes[0]
    if (!fallbackType) {
      throw new Error("Cannot select a bumper type from an empty pool")
    }

    let totalWeight = 0
    for (const t of availableTypes) {
      totalWeight += weights[t.effect] ?? 1
    }

    if (totalWeight <= 0) return fallbackType

    let roll = Math.random() * totalWeight

    for (const t of availableTypes) {
      roll -= weights[t.effect] ?? 1
      if (roll <= 0) return t
    }

    return fallbackType
  }
}
